import React, { useEffect, Dispatch, SetStateAction } from 'react';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CustomSelect from '../ui/CustomSelect';

interface SettingsPanelProps {
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
  variations: number;
  setVariations: Dispatch<SetStateAction<number>>;
  sceneStyle: string;
  setSceneStyle: Dispatch<SetStateAction<string>>;
  mode: string;
  setMode: Dispatch<SetStateAction<string>>;
  showGenerateButton?: boolean;
  onGenerate?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  prompt,
  setPrompt,
  variations,
  setVariations,
  sceneStyle,
  setSceneStyle,
  mode,
  setMode
}) => {
  // Mode restrictions hook
  const modeRestrictions = useModeRestrictions();

  // Handle mode restrictions - automatically switch to available mode if current is restricted
  useEffect(() => {
    if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
      // Switch to the first available mode (Performance is always available)
      const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
      if (availableMode) {
        setMode(availableMode.value);
      }
    }
  }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions.canUseMode, setMode]);

  const decreaseVariations = () => {
    if (variations > 1) {
      setVariations(variations - 1);
    }
  };

  const increaseVariations = () => {
    if (variations < 5) {
      setVariations(variations + 1);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-text-heading mb-1">Scene Style</label>
          <CustomSelect
            value={sceneStyle}
            onChange={(value) => setSceneStyle(value)}
            options={[
              { value: 'lifestyle', label: 'Lifestyle' },
              { value: 'studio', label: 'Studio' },
              { value: 'outdoor', label: 'Outdoor' },
              { value: 'minimal', label: 'Minimal' },
              { value: 'luxury', label: 'Luxury' }
            ]}
            className="w-full"
          />
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-text-heading mb-1">
            Mode
            {!modeRestrictions.hasInitialLoad && (
              <span className="ml-2 text-xs text-text-description">(Loading...)</span>
            )}
          </label>
          <div className="relative">
            <CustomSelect
              value={mode}
              onChange={(value) => setMode(value)}
              options={modeRestrictions.availableModes.map((modeOption) => {
                let planRequirement = '';
                if (modeOption.disabled && modeOption.requiresPlan) {
                  const minPlan = modeOption.requiresPlan[0];
                  switch (minPlan) {
                    case 'plus':
                      planRequirement = ' (Plus Plan Required)';
                      break;
                    case 'pro':
                      planRequirement = ' (Pro Plan Required)';
                      break;
                    case 'enterprise':
                      planRequirement = ' (Enterprise Plan Required)';
                      break;
                    default:
                      planRequirement = ' (Upgrade Required)';
                  }
                }
                return {
                  value: modeOption.value,
                  label: `${modeOption.label}${planRequirement}`,
                  disabled: modeOption.disabled
                };
              })}
              disabled={!modeRestrictions.hasInitialLoad}
              className="w-full"
            />
            {modeRestrictions.error && (
              <p className="text-xs text-red-600 mt-1">{modeRestrictions.error}</p>
            )}
            {!modeRestrictions.canUseMode(mode) && modeRestrictions.hasInitialLoad && (
              <p className="text-xs text-orange-600 mt-1">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                {modeRestrictions.getRestrictedModeMessage(mode)}
              </p>
            )}
          </div>
        </div>

        <div className="w-full md:w-[140px]">
          <label className="block text-sm font-medium text-text-heading mb-1">Number of Images</label>
          <div className="flex items-center justify-between border border-accent-taupe rounded-lg h-[42px] bg-white">
            <button
              onClick={decreaseVariations}
              disabled={variations <= 1}
              className={`flex-1 h-full flex items-center justify-center rounded-l-lg ${variations <= 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-text-heading hover:bg-accent-sand transition-colors'
                }`}
              aria-label="Decrease number of images"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M3.75 12a.75.75 0 01.75-.75h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="flex-1 text-center font-medium text-text-heading select-none">
              {variations}
            </div>

            <button
              onClick={increaseVariations}
              disabled={variations >= 5}
              className={`flex-1 h-full flex items-center justify-center rounded-r-lg ${variations >= 5
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-text-heading hover:bg-accent-sand transition-colors'
                }`}
              aria-label="Increase number of images"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v7.5h7.5a.75.75 0 010 1.5h-7.5v7.5a.75.75 0 01-1.5 0v-7.5h-7.5a.75.75 0 010-1.5h7.5v-7.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-heading mb-1">Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe your desired scene (e.g., perfume bottle in a landscape)"
          className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 min-h-[80px] resize-y"
        />
      </div>
    </div>
  );
};

export default SettingsPanel; 